import type { TagReference } from "@/domains/documents/components/molecules/DocumentTags";
import {
  resolveDocumentBreadcrumb,
  resolveDocumentFolderPath,
  type FolderPathSegment,
} from "@/domains/documents/utils/folderPathResolver";
import { useMemo } from "react";

import type { documentTableOptions } from "./documentTableOptions";
import type { useTableBasics } from "./useTableBasics";
import type { useTableDocumentKinds } from "./useTableDocumentKinds";
import type { useTableTagActions } from "./useTableTagActions";
type Options = Pick<ReturnType<typeof useTableDocumentKinds>, "displayDocuments"> &
  Pick<ReturnType<typeof useTableTagActions>, "resolveTags"> &
  Pick<ReturnType<typeof documentTableOptions>, "store"> &
  Pick<ReturnType<typeof useTableBasics>, "language">;
export function useTableDocumentMetadata(options: Options) {
  const { displayDocuments, resolveTags, store, language } = options;
  // Memoize tags for all documents to avoid recalculating on every render
  // resolveTags also changes with the current store and language.
  const documentTagsMap = useMemo(() => {
    const map = new Map<string, TagReference[]>();
    displayDocuments.forEach((doc) => {
      // Skip documents with empty IDs
      if (doc.id && String(doc.id).trim()) {
        map.set(doc.id, resolveTags(doc));
      }
    });
    return map;
  }, [displayDocuments, resolveTags]);

  // Memoize folder paths for all documents (for tooltip breadcrumb)
  // Resolves: "Project Name / folder / subfolder / filename"
  const documentFolderPaths = useMemo(() => {
    const map = new Map<string, string>();
    if (!store) return map;
    displayDocuments.forEach((doc) => {
      if (!doc.id || !String(doc.id).trim()) return;
      const path = resolveDocumentFolderPath(
        { project_id: doc.project_id, folder_id: doc.folder_id, filename: doc.filename },
        store.tags.projects as Map<string, { name: string }>,
        store.tags.folders as Map<
          string,
          { folder_path?: string | null; name?: string; project_id?: number | null }
        >
      );
      if (path) map.set(doc.id, path);
    });
    return map;
  }, [displayDocuments, store]);

  // Memoize structured breadcrumb segments for interactive tooltip navigation
  const documentBreadcrumbs = useMemo(() => {
    const map = new Map<string, FolderPathSegment[]>();
    if (!store) return map;
    displayDocuments.forEach((doc) => {
      if (!doc.id || !String(doc.id).trim()) return;
      const segments = resolveDocumentBreadcrumb(
        { project_id: doc.project_id, folder_id: doc.folder_id, filename: doc.filename },
        store.tags.projects as Map<string, { name: string }>,
        store.tags.folders as Map<
          string,
          { folder_path?: string | null; name?: string; project_id?: number | null }
        >
      );
      if (segments) map.set(doc.id, segments);
    });
    return map;
  }, [displayDocuments, store]);
  return { documentTagsMap, documentFolderPaths, documentBreadcrumbs };
}
